// 3D podium for final results. 1st/2nd/3rd (we accept top 3 from leaderboard) rise onto
// their blocks; 1st gets gold shimmer.

import {
  BoxGeometry,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  PlaneGeometry,
} from 'three';
import type { LbRow } from '@sg/shared';
import { avatarTexture } from '../ui/avatar.js';
import { clamp01, easeOutCubic } from './lib/easing.js';
import { textTexture } from './lib/text-texture.js';
import {
  COLOR_GOLD,
  COLOR_STAGE_BLUE,
  COLOR_STAGE_BLUE_DIM,
  FONT_DISPLAY,
  FONT_SLAB,
  STAGE_HEIGHT,
  STAGE_WIDTH,
} from './lib/theme.js';

export interface PodiumHandle {
  group: Object3D;
  show: (rows: LbRow[]) => void;
  hide: () => void;
  tick: (nowMs: number) => void;
  dispose: () => void;
}

const BLOCK_W = 320;
const AV = 220;

interface Column {
  container: Object3D;
  blockMesh: Mesh;
  avMat: MeshBasicMaterial;
  nameMat: MeshBasicMaterial;
  scoreMat: MeshBasicMaterial;
  targetY: number;
  currentY: number;
  startY: number;
}

export function createPodium(): PodiumHandle {
  const group = new Object3D();
  group.name = 'podium';
  group.visible = false;

  const scrim = new Mesh(
    new PlaneGeometry(STAGE_WIDTH, STAGE_HEIGHT),
    new MeshBasicMaterial({ color: 0x02051a, transparent: true, opacity: 0 }),
  );
  scrim.renderOrder = 100;
  group.add(scrim);

  const titleMat = new MeshBasicMaterial({ transparent: true, opacity: 0 });
  const title = new Mesh(new PlaneGeometry(1200, 160), titleMat);
  title.position.set(0, 380, 0);
  title.renderOrder = 110;
  group.add(title);
  const titleTex = textTexture('RESULTS', {
    font: `160px ${FONT_DISPLAY}`,
    color: '#FFC43D',
    width: 1200,
    height: 160,
    weight: 'bold',
    stroke: '#000000',
    strokeWidth: 5,
  });
  titleMat.map = titleTex;
  titleMat.needsUpdate = true;

  const columns: Column[] = [];

  function clearCols(): void {
    for (const c of columns) {
      c.avMat.dispose();
      c.nameMat.dispose();
      c.scoreMat.dispose();
      (c.blockMesh.material as MeshBasicMaterial).dispose();
      c.blockMesh.geometry.dispose();
      group.remove(c.container);
    }
    columns.length = 0;
  }

  function buildColumn(rank: number, row: LbRow): Column {
    const blockHeights: Record<number, number> = { 1: 260, 2: 180, 3: 120 };
    const xPositions: Record<number, number> = { 1: 0, 2: -420, 3: 420 };
    const bh = blockHeights[rank] ?? 100;
    const x = xPositions[rank] ?? 0;
    const container = new Object3D();
    const color = rank === 1 ? COLOR_GOLD : rank === 2 ? COLOR_STAGE_BLUE : COLOR_STAGE_BLUE_DIM;

    const blockGeom = new BoxGeometry(BLOCK_W, bh, 180);
    const blockMat = new MeshBasicMaterial({ color, transparent: true });
    const blockMesh = new Mesh(blockGeom, blockMat);
    const baseY = -STAGE_HEIGHT / 2 + bh / 2 + 80;
    blockMesh.position.set(x, baseY, -20);
    container.add(blockMesh);

    const avMat = new MeshBasicMaterial({ transparent: true });
    const avMesh = new Mesh(new PlaneGeometry(AV, AV), avMat);
    avMesh.position.set(x, baseY + bh / 2 + AV / 2 + 20, 0);
    avMat.map = avatarTexture(row.avatar_seed, { size: 256 });
    avMat.needsUpdate = true;
    container.add(avMesh);

    // Name plane is widened (BLOCK_W + 200) so longer display names don't
    // clip against the block edge, and the font is sized down via a rough
    // character-count heuristic (long names shrink to fit).
    const NAME_PLANE_W = BLOCK_W + 200;
    const nameFontPx = row.display_name.length > 12 ? 36 : row.display_name.length > 9 ? 42 : 46;
    const nameMat = new MeshBasicMaterial({ transparent: true });
    const nameMesh = new Mesh(new PlaneGeometry(NAME_PLANE_W, 60), nameMat);
    nameMesh.position.set(x, baseY + bh / 2 + AV + 60, 0);
    nameMat.map = textTexture(row.display_name, {
      font: `${nameFontPx}px ${FONT_SLAB}`,
      color: '#FFFFFF',
      width: NAME_PLANE_W,
      height: 60,
      weight: 'bold',
      stroke: '#000000',
      strokeWidth: 3,
    });
    nameMat.needsUpdate = true;
    container.add(nameMesh);

    // Score plane is widened (BLOCK_W + 240) and font shrinks for 5+ digit
    // scores so e.g. "11300" doesn't clip the trailing glyph. Common case
    // (1130) renders cleanly at 72px with the wider plane.
    const SCORE_PLANE_W = BLOCK_W + 240;
    const scoreText = `${row.total_points}`;
    const scoreFontPx = scoreText.length > 6 ? 56 : scoreText.length > 5 ? 64 : 72;
    const scoreMat = new MeshBasicMaterial({ transparent: true });
    const scoreMesh = new Mesh(new PlaneGeometry(SCORE_PLANE_W, 90), scoreMat);
    scoreMesh.position.set(x, baseY, 0.1);
    scoreMat.map = textTexture(scoreText, {
      font: `${scoreFontPx}px ${FONT_DISPLAY}`,
      color: '#FFFFFF',
      width: SCORE_PLANE_W,
      height: 90,
      weight: 'bold',
      stroke: '#000000',
      strokeWidth: 3,
    });
    scoreMat.needsUpdate = true;
    container.add(scoreMesh);

    group.add(container);
    const startY = -STAGE_HEIGHT;
    container.position.y = startY;
    const col: Column = Object.create(null) as Column;
    col.container = container;
    col.blockMesh = blockMesh;
    col.avMat = avMat;
    col.nameMat = nameMat;
    col.scoreMat = scoreMat;
    col.targetY = 0;
    col.currentY = startY;
    col.startY = startY;
    return col;
  }

  let startMs: number | null = null;

  function show(rows: LbRow[]): void {
    group.visible = true;
    startMs = performance.now();
    clearCols();
    const top3 = rows.slice(0, 3);
    top3.forEach((r, i) => {
      const rank = i + 1;
      columns.push(buildColumn(rank, r));
    });
  }

  function hide(): void {
    group.visible = false;
    startMs = null;
  }

  function tick(nowMs: number): void {
    if (!group.visible || startMs === null) return;
    const elapsed = nowMs - startMs;
    (scrim.material as MeshBasicMaterial).opacity = 0.9 * clamp01(elapsed / 400);
    titleMat.opacity = clamp01(elapsed / 600);
    columns.forEach((col, i) => {
      const delay = 400 + i * 400;
      const t = clamp01((elapsed - delay) / 900);
      const e = easeOutCubic(t);
      col.currentY = col.startY + (col.targetY - col.startY) * e;
      col.container.position.y = col.currentY;
    });
  }

  return {
    group,
    show,
    hide,
    tick,
    dispose() {
      scrim.geometry.dispose();
      (scrim.material as MeshBasicMaterial).dispose();
      titleMat.dispose();
      title.geometry.dispose();
      clearCols();
    },
  };
}
